/**
 * /medical-translator-vietnam
 *
 * Single SEO landing page — Medical Translator in Vietnam.
 * Data-driven, reuses existing site components & branding. No new design work.
 *
 * Safety wording (per copy spec):
 *   • "Translator support where available" — never "always available".
 *   • Does not claim all translators are certified.
 *   • Independent platform — not a hospital/clinic.
 *   • Does not provide diagnosis or replace medical advice.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, ShieldCheck, MessageCircle, Languages, ClipboardCheck,
  AlertTriangle, CheckCircle2, Headphones, Heart,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  UrgentIntro, UrgentCardGrid, UrgentSteps, UrgentFaq, UrgentEditorial,
  UrgentCtaBand,
} from '../components/UrgentCareSections';
import { useContactInfo, getWhatsAppHref } from '../contexts/ContactInfoContext';
import { useCatalog } from '../context/CatalogContext';
import { getInfoLandingImage } from '../lib/imageOverrides';

const SITE = 'https://vietclinics.com';
const SLUG = '/medical-translator-vietnam';

const HERO_IMAGE =
  'https://images.pexels.com/photos/4226140/pexels-photo-4226140.jpeg?auto=compress&cs=tinysrgb&w=1400';
const HERO_FALLBACK =
  'https://images.pexels.com/photos/7088530/pexels-photo-7088530.jpeg?auto=compress&cs=tinysrgb&w=1400';

const COMMUNICATION_HELP_AREAS = [
  { title: 'Doctor appointment translation',         body: 'Translation and communication support during in-person doctor appointments — where translator availability is confirmed for your city, treatment and timing.' },
  { title: 'Clinic & hospital communication',        body: 'Help coordinating with front-desk teams, nurses, doctors and patient coordinators so check-in, paperwork and next steps stay clear.' },
  { title: 'Online consultation support',             body: 'For video consultations, we help prepare questions, share basic information with the doctor in advance, and stay reachable if anything needs clarifying.' },
  { title: 'Question preparation',                    body: 'We help you prepare the right questions to ask before, during and after the appointment — so you do not leave with unanswered doubts.' },
  { title: 'Treatment plan & price explanation',      body: 'Once the doctor proposes a plan, we help you read it in plain English and clarify anything that is not obvious — what is included, recovery time, and follow-up.' },
  { title: 'Follow-up & aftercare communication',     body: 'After treatment, we help with prescription clarifications, follow-up bookings and travel insurance documentation in clear English.' },
  { title: 'Travel & appointment coordination',       body: 'Where useful, we coordinate appointment timing, transfers and accommodation around your medical schedule.' },
];

const SERVICE_BLOCKS = [
  { to: '/cosmetic-surgery-in-vietnam',           label: 'Cosmetic surgery translator support',  sub: 'Surgeon consultations, pre-op planning and aftercare instructions' },
  { to: '/dental-treatments-in-vietnam',          label: 'Dental treatment translator support',  sub: 'Implants, veneers, crowns — treatment plan clarification' },
  { to: '/urgent-medical-care',                   label: 'Urgent medical translator support',    sub: 'For travelers & expats — hotel doctor, IV therapy, telehealth' },
  { to: '/preventive-care-treatments-in-vietnam', label: 'Health checkup & specialist support',  sub: 'Executive health checks and specialist appointments' },
  { to: '/hair-restoration-in-vietnam',           label: 'Hair transplant consultation support',  sub: 'Graft planning, hairline design and aftercare in clear English' },
  { to: '/eye-care-treatments-in-vietnam',        label: 'Eye treatment consultation support',   sub: 'LASIK, cataract, glaucoma and retina consultations' },
];

const PREP_CHECKLIST = [
  'Your main symptoms or concerns',
  'Your medical history',
  'Current medications you take',
  'Allergies (medication, food, latex, anaesthesia)',
  'Previous surgery or treatment relevant to this visit',
  'Photos or scans if relevant (X-ray, MRI, CT, dental scans)',
  'Your treatment goals and expectations',
  'Your budget range if relevant',
  'Preferred city, clinic or hospital',
  'Questions you want to ask the doctor',
];

const QUESTIONS_TRANSLATOR_HELPS = [
  'What is my diagnosis or possible condition?',
  'What tests or scans are needed?',
  'What treatment options are available?',
  'What are the risks and possible complications?',
  'What is included in the price?',
  'How long is recovery?',
  'How many appointments are needed?',
  'What should I avoid before and after treatment?',
  'Who do I contact if I have concerns?',
  'Can I receive written instructions or a treatment plan?',
];

const WHO_ITS_FOR = [
  'International patients planning treatment in Vietnam',
  'Expats living in Vietnam',
  'Travelers needing medical help in-country',
  'Medical tourists comparing options across cities',
  'Patients considering cosmetic or plastic surgery',
  'Patients considering dental treatment',
  'Patients needing urgent (non-emergency) medical support',
  'Patients seeking a second opinion',
  'Patients who do not speak Vietnamese',
  'Anyone who feels unsure about medical communication',
];

const CITIES = [
  { name: 'Ho Chi Minh City', region: 'Southern Vietnam' },
  { name: 'Hanoi',            region: 'Northern Vietnam' },
  { name: 'Da Nang',          region: 'Central Coast' },
  { name: 'Hoi An',           region: 'Central Coast' },
  { name: 'Vung Tau',         region: 'Southern Coast' },
  { name: 'Nha Trang',        region: 'South Central Coast' },
  { name: 'Phu Quoc',         region: 'Island' },
];

const FAQ = [
  { q: 'Can VietClinics provide a medical translator in Vietnam?',          a: 'VietClinics provides translator and communication support where available, based on city, treatment type, appointment timing and provider availability. We confirm what support is realistically possible for your case before any appointment is booked. We do not claim translators are universally available or that every translator is independently certified.' },
  { q: 'Do I need a translator for a doctor appointment in Vietnam?',       a: 'It depends. Many of our partner doctors and clinics work in English, which may be enough for straightforward consultations. For complex cases — surgery, dental treatment plans, second opinions or hospital admissions — a translator (or English-speaking patient coordinator) often makes a real difference. We help you decide what is suitable for your specific appointment.' },
  { q: 'Can VietClinics help me speak with a hospital or clinic?',          a: 'Yes. We help coordinate with front-desk teams, nurses, doctors and patient coordinators so check-in, paperwork, scheduling and next steps stay clear. Most of this is handled in English by our coordinator team, with Vietnamese translation arranged where needed.' },
  { q: 'Can VietClinics help with online consultation translation support?', a: 'Yes. For video consultations, we help prepare questions, share basic information with the doctor in advance and (where useful) stay reachable during or after the consultation to clarify anything. Online consultations are often the fastest path to clinical advice while travelling.' },
  { q: 'Can VietClinics help me prepare questions for the doctor?',          a: 'Yes. We share a tailored question set before your appointment so you can be confident you have covered the essentials — diagnosis, treatment options, risks, what is included in the price, recovery and follow-up.' },
  { q: 'Can VietClinics explain my treatment plan?',                          a: 'We help you read the doctor\'s written treatment plan in plain English and clarify anything that is unclear — but we do not provide medical interpretation of the plan itself. Clinical decisions and detailed medical advice come from the doctor.' },
  { q: 'Can VietClinics help with cosmetic surgery translator support?',     a: 'Yes — see our Cosmetic Surgery in Vietnam hub. Many partner surgeons work in English, but for surgical consent, pre-op planning and aftercare instructions a translator (or English-speaking coordinator) is often valuable. We confirm what is available for your specific surgeon.' },
  { q: 'Can VietClinics help with dental treatment translator support?',     a: 'Yes — see our Dental Treatment in Vietnam hub. For complex dental plans (implants, full-mouth restoration, multi-visit treatments), clear communication matters. We help coordinate translation or English-speaking support where needed.' },
  { q: 'Is VietClinics a clinic or hospital?',                                a: 'No. VietClinics is an independent healthcare coordination platform. We do not provide medical advice, diagnosis or treatment ourselves. We help with communication, coordination and patient support.' },
  { q: 'Does VietClinics provide medical advice?',                            a: 'No. VietClinics does not provide medical advice, diagnosis or treatment recommendations. Translator and communication support helps you understand and communicate more clearly, but all medical advice, diagnosis and treatment decisions must come from licensed healthcare professionals.' },
];

const RELATED_LINKS = [
  { to: '/patient-support-vietnam',          label: 'Patient Support in Vietnam',         sub: 'Doctor recommendations, treatment coordination and aftercare' },
  { to: '/medical-tourism-support-vietnam',  label: 'Medical Tourism Support in Vietnam', sub: 'Treatment, travel & patient coordination for international patients' },
  { to: '/doctor-recommendation-vietnam',    label: 'Doctor Recommendation in Vietnam',   sub: 'Find suitable licensed doctors, clinics and specialists' },
  { to: '/how-it-works',                     label: 'How VietClinics Works',              sub: 'The 4-step concierge process explained' },
  { to: '/contact',                          label: 'Contact VietClinics',                sub: 'Talk to a coordinator on WhatsApp or email' },
  { to: '/cosmetic-surgery-in-vietnam',      label: 'Cosmetic Surgery in Vietnam',        sub: 'Verified plastic surgeons at JCI-accredited hospitals' },
  { to: '/dental-treatments-in-vietnam',     label: 'Dental Treatment in Vietnam',        sub: 'Implants, veneers and full-mouth restoration' },
  { to: '/urgent-medical-care',              label: 'Urgent Medical Care in Vietnam',     sub: 'Hotel doctor, IV therapy & English-speaking telehealth' },
  { to: '/preventive-care-treatments-in-vietnam', label: 'Health Checkups in Vietnam',    sub: 'Executive health and specialist screenings' },
  { to: '/hair-restoration-in-vietnam',      label: 'Hair Transplant in Vietnam',         sub: 'Sapphire FUE & DHI by Korean & Turkish-trained surgeons' },
  { to: '/eye-care-treatments-in-vietnam',   label: 'Eye Treatment in Vietnam',           sub: 'LASIK, cataract and retina specialists' },
];

const EDITORIAL = {
  reviewedBy: 'VietClinics Patient Concierge Team',
  role: 'Translation & communication coordinators',
  methodology:
    'Translator and English-speaking support is confirmed per case based on city, treatment type, appointment timing and provider availability. We are transparent about what is realistically available before any appointment is booked — we never promise universal coverage or claim all translators are independently certified.',
  lastUpdated: '2026-02-15',
};

const MedicalTranslatorVietnamPage = () => {
  const { contactInfo } = useContactInfo();
  const { overrides } = useCatalog();
  const heroImage = getInfoLandingImage(overrides, 'medical-translator', HERO_IMAGE);
  const whatsappHref = getWhatsAppHref(
    contactInfo,
    'Hi VietClinics, I need medical translator support in Vietnam.'
  );
  const canonical = `${SITE}${SLUG}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        '@id': `${canonical}#service`,
        serviceType: 'Medical Translator & Communication Support',
        provider: { '@type': 'Organization', name: 'VietClinics' },
        areaServed: [{ '@type': 'Country', name: 'Vietnam' }],
        url: canonical,
        description:
          'Translator and communication support for international patients, expats and travelers in Vietnam. Doctor appointment translation, hospital communication, online consultation support and treatment plan explanation in clear English.',
      },
      {
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: 'Medical Translator & Communication Support in Vietnam',
        description:
          'VietClinics helps international patients, expats, and travelers in Vietnam access medical translator support, communicate with doctors, prepare questions, arrange appointments, and understand treatment plans more clearly.',
        inLanguage: 'en',
        dateModified: EDITORIAL.lastUpdated,
        lastReviewed: EDITORIAL.lastUpdated,
        reviewedBy: { '@type': 'Organization', name: EDITORIAL.reviewedBy },
      },
      {
        '@type': 'FAQPage',
        '@id': `${canonical}#faq`,
        mainEntity: FAQ.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonical}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Patient Support', item: `${SITE}/patient-support-vietnam` },
          { '@type': 'ListItem', position: 3, name: 'Medical Translator & Communication Support', item: canonical },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Medical Translator &amp; Communication Support in Vietnam | VietClinics</title>
        <meta
          name="description"
          content="VietClinics helps international patients, expats, and travelers in Vietnam access medical translator and communication support — doctor appointment translation, hospital communication, question preparation, treatment plan explanation, and online consultation support."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Medical Translator & Communication Support in Vietnam | VietClinics" />
        <meta
          property="og:description"
          content="Translator and communication support for international patients, expats and travelers — doctor appointments, hospitals, online consultations and treatment plan explanation in Vietnam."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={heroImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-white" data-testid="med-translator-hero">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, #F0FAF8 0%, #DCEFEC 60%, #ffffff 100%)',
          }}
        />
        <div className="relative container mx-auto px-4 max-w-7xl py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[11px] font-bold text-[#1A8F8A] uppercase tracking-widest mb-4 inline-block bg-white/80 backdrop-blur px-3 py-1.5 rounded-full border border-[#DCEFEC]">
                Medical translator &amp; communication support · Vietnam
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.05] mb-5">
                Medical Translator &amp; Communication Support in Vietnam
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl mb-7">
                Translator and communication support for international patients,
                expats, travelers and medical tourists arranging doctor appointments,
                clinic visits, hospital care, dental treatment, cosmetic surgery,
                hair transplants, eye treatment, health checkups and urgent medical
                support in Vietnam — plus help preparing questions, explaining
                symptoms and understanding treatment plans, prices, risks and aftercare.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                {whatsappHref && (
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-[#1FA7A1] hover:bg-[#138F8A] text-white font-bold rounded-full px-6 py-3.5 transition-colors"
                    data-testid="med-translator-cta-request"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Request Translator Support
                  </a>
                )}
                <Link
                  to="/doctor-recommendation-vietnam"
                  className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 border border-slate-200 hover:border-[#1FA7A1] font-bold rounded-full px-6 py-3.5 transition-colors"
                  data-testid="med-translator-cta-appointment"
                >
                  Book Doctor Appointment Support
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <div
              className="relative rounded-3xl overflow-hidden bg-slate-100"
              style={{ aspectRatio: '4 / 3' }}
            >
              <img
                src={heroImage}
                onError={(e) => { e.currentTarget.src = HERO_FALLBACK; }}
                alt="Medical translator support for international patients in Vietnam"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST STRIP ──────────────────────────────────────────────────── */}
      <section className="bg-white border-y border-slate-100" data-testid="med-translator-trust">
        <div className="container mx-auto px-4 max-w-7xl py-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: ShieldCheck, label: 'Verified partner network' },
              { icon: Languages,   label: 'Translator support where available' },
              { icon: Headphones,  label: 'English-speaking coordinators' },
              { icon: Heart,       label: 'Independent — not a hospital or clinic' },
            ].map(({ icon: Icon, label }, i) => (
              <div key={label} className="flex items-center gap-3" data-testid={`med-translator-trust-${i + 1}`}>
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: '#DCEFEC' }}
                >
                  <Icon className="h-4 w-4 text-[#1A8F8A]" />
                </div>
                <p className="text-xs font-bold text-slate-800 leading-tight">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INTRODUCTION ─────────────────────────────────────────────────── */}
      <UrgentIntro
        heading="Medical appointments are stressful — language barriers should not make it worse."
        paragraphs={[
          'Going to a doctor in another country can feel difficult when there is a language barrier. You may need help explaining symptoms, sharing your medical history, asking about allergies and medication, describing your treatment goals, and understanding the doctor\'s recommendations and follow-up instructions.',
          'VietClinics helps international patients, expats and travelers communicate more clearly with clinics, doctors, dentists, surgeons and hospitals in Vietnam. Our coordinators work in English by default, and we arrange Vietnamese translator support for medical appointments where it is needed and available.',
          'We are an independent healthcare coordination platform — not a clinic or hospital. Translator and communication support helps you understand and communicate more clearly, but all medical advice, diagnosis and treatment decisions come from the licensed healthcare professionals at our partner clinics and hospitals.',
        ]}
        testId="med-translator-intro"
      />

      {/* ── WHY TRANSLATOR SUPPORT MATTERS ───────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="med-translator-why">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Why medical translator support matters
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-6">
            Clear communication is part of safe care
          </h2>
          <div className="space-y-5 text-base lg:text-lg text-slate-600 leading-relaxed mb-8">
            <p>
              Clear communication helps reduce misunderstandings and helps you feel
              more confident before making medical decisions. Even small details — a
              forgotten allergy, an unclear medication instruction, a misunderstood
              recovery timeline — can affect outcomes.
            </p>
            <p>
              Translator and communication support typically helps with:
            </p>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3">
            {[
              'Explaining your symptoms clearly',
              'Sharing your medical history accurately',
              'Understanding doctor recommendations',
              'Asking about risks and aftercare',
              'Understanding treatment plans and pricing',
              'Preparing for surgery or dental treatment',
              'Understanding follow-up instructions',
            ].map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-3 bg-white border border-slate-100 rounded-2xl p-4"
                style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
                data-testid={`med-translator-why-item-${i + 1}`}
              >
                <CheckCircle2 className="h-5 w-5 text-[#1FA7A1] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700 font-semibold leading-snug">{item}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── HOW WE HELP WITH COMMUNICATION ───────────────────────────────── */}
      <UrgentCardGrid
        eyebrow="How VietClinics helps with communication"
        heading="Practical communication support across your healthcare journey"
        items={COMMUNICATION_HELP_AREAS}
        testId="med-translator-how"
      />

      {/* ── TRANSLATOR SUPPORT FOR DOCTOR APPOINTMENTS ───────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="med-translator-appointments">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Translator support for doctor appointments
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-6">
            Doctor appointment translation, made simpler
          </h2>
          <div className="space-y-5 text-base lg:text-lg text-slate-600 leading-relaxed">
            <p>
              VietClinics can help arrange or support communication for doctor
              appointments in Vietnam, depending on the city, the treatment type
              and provider availability. Many of our partner doctors and clinics
              work in English, so translation may not always be needed — but for
              complex consultations, surgical consent, hospital admissions or
              multi-visit treatment plans, having dedicated communication support
              often makes the experience much smoother.
            </p>
            <p>
              We confirm in advance what kind of translator or English-speaking
              support is realistically available for your specific appointment, so
              there are no surprises on the day.
            </p>
          </div>
        </div>
      </section>

      {/* ── TRANSLATOR SUPPORT FOR HOSPITALS & CLINICS ───────────────────── */}
      <section className="py-16 lg:py-20 bg-[#F7FAF9]" data-testid="med-translator-hospitals">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Translator support for hospitals and clinics
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-6">
            From front desk to discharge — we help every step
          </h2>
          <div className="space-y-5 text-base lg:text-lg text-slate-600 leading-relaxed">
            <p>
              A hospital or clinic visit involves more than just talking to the
              doctor. You may need help communicating with front-desk teams,
              nurses, patient coordinators, billing staff and pharmacy. Each of
              these touchpoints matters — for paperwork, scheduling, billing
              accuracy and clear medication instructions.
            </p>
            <p>
              VietClinics coordinates communication with our verified partner
              network in English by default, and arranges Vietnamese translator
              support where it adds value. We stay with you from booking through
              follow-up so the experience feels supported, not improvised.
            </p>
          </div>
        </div>
      </section>

      {/* ── BY MEDICAL SERVICE ───────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="med-translator-by-service">
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Support for different medical services
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            Communication support across treatment types
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICE_BLOCKS.map((b) => (
              <Link
                key={b.to}
                to={b.to}
                className="group bg-white hover:bg-[#F0FAF8] border border-slate-100 hover:border-[#DCEFEC] rounded-2xl p-5 transition-colors flex items-start justify-between gap-4"
                style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
                data-testid={`med-translator-service-${b.to.replace(/\W+/g, '-').replace(/^-|-$/g, '')}`}
              >
                <div>
                  <p className="font-extrabold text-slate-900 text-sm leading-tight">{b.label}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-snug">{b.sub}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-[#1FA7A1] flex-shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── PREP CHECKLIST ───────────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-[#F7FAF9]" data-testid="med-translator-prep">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Before your medical appointment
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            What to prepare in advance
          </h2>
          <ul className="grid sm:grid-cols-2 gap-3">
            {PREP_CHECKLIST.map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-3 bg-white border border-slate-100 rounded-2xl p-4"
                style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
                data-testid={`med-translator-prep-${i + 1}`}
              >
                <ClipboardCheck className="h-5 w-5 text-[#1FA7A1] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700 font-semibold leading-snug">{item}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── QUESTIONS TRANSLATOR HELPS YOU ASK ───────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="med-translator-questions">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Questions translator support can help you ask
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            Be prepared, be confident
          </h2>
          <div className="space-y-3">
            {QUESTIONS_TRANSLATOR_HELPS.map((q, i) => (
              <div
                key={i}
                className="flex items-start gap-3 bg-white border border-slate-100 rounded-xl px-5 py-4"
                style={{ boxShadow: '0 2px 12px rgba(15,42,85,0.04)' }}
                data-testid={`med-translator-question-${i + 1}`}
              >
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white font-extrabold text-xs flex-shrink-0 mt-0.5"
                  style={{ background: '#1FA7A1' }}
                >
                  {i + 1}
                </span>
                <p className="text-sm md:text-base text-slate-700 font-semibold leading-snug">{q}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ONLINE CONSULTATION SUPPORT ──────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-[#F7FAF9]" data-testid="med-translator-online">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Online consultation communication support
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-6">
            Talk to a doctor — without travel
          </h2>
          <div className="space-y-5 text-base lg:text-lg text-slate-600 leading-relaxed">
            <p>
              Many patients prefer an online consultation before travelling or
              before committing to a clinic. VietClinics helps coordinate online
              video consultations with suitable doctors in Vietnam, prepare your
              questions in advance, share basic case information with the doctor
              so the call is efficient, and stay reachable to clarify anything
              afterwards.
            </p>
            <p>
              Online consultation is often the fastest path to clinical advice —
              especially useful for non-urgent symptoms, second opinions, pre-op
              questions, and follow-up checks once you have returned home.
            </p>
          </div>
        </div>
      </section>

      {/* ── WHO IT'S FOR ─────────────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="med-translator-who">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Who this service is for
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            We support patients with very different needs
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {WHO_ITS_FOR.map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-3 bg-white border border-slate-100 rounded-2xl p-4"
                style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
                data-testid={`med-translator-who-${i + 1}`}
              >
                <Languages className="h-5 w-5 text-[#1A8F8A] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700 font-semibold leading-snug">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CITIES COVERED ───────────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-[#F7FAF9]" data-testid="med-translator-cities">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Cities covered
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            Medical translator support across Vietnam
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {CITIES.map((c) => (
              <div
                key={c.name}
                className="bg-white border border-slate-100 rounded-2xl px-4 py-3"
                data-testid={`med-translator-city-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <p className="font-extrabold text-slate-900 text-sm">{c.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{c.region}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-5 max-w-2xl">
            Translator availability varies by city, treatment type, appointment
            timing and provider. We confirm what is realistically available for
            your specific case before any appointment is booked.
          </p>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <UrgentFaq
        heading="Medical translator &amp; communication support in Vietnam — frequently asked"
        items={FAQ}
        testId="med-translator-faq"
      />

      {/* ── RELATED INTERNAL LINKS ───────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="med-translator-related">
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Continue exploring VietClinics
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            More patient support &amp; treatment guides
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {RELATED_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="group bg-[#F0FAF8] hover:bg-[#DCEFEC] border border-[#DCEFEC] rounded-2xl p-5 transition-colors flex items-start justify-between gap-4"
                data-testid={`med-translator-related-${link.to.replace(/\W+/g, '-').replace(/^-|-$/g, '')}`}
              >
                <div>
                  <p className="font-extrabold text-slate-900 text-sm leading-tight">{link.label}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-snug">{link.sub}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-[#1FA7A1] flex-shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── EDITORIAL ────────────────────────────────────────────────────── */}
      <UrgentEditorial editorial={EDITORIAL} testId="med-translator-editorial" />

      {/* ── IMPORTANT DISCLAIMER ─────────────────────────────────────────── */}
      <section className="py-10 bg-white" data-testid="med-translator-disclaimer-section">
        <div className="container mx-auto px-4 max-w-4xl">
          <div
            className="bg-amber-50/60 border border-amber-100 rounded-2xl p-5 lg:p-6 flex gap-3"
            data-testid="med-translator-disclaimer"
          >
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm leading-relaxed text-amber-900">
              <p className="font-bold mb-1">Important notice</p>
              <p>
                VietClinics is an independent healthcare coordination platform.
                VietClinics is <strong>not a hospital or clinic</strong> and does
                <strong> not provide medical diagnosis</strong>. Translator and
                communication support helps patients understand and communicate
                more clearly, but all medical advice, diagnosis and treatment
                decisions must come from licensed healthcare professionals. For
                life-threatening emergencies in Vietnam, dial <strong>115</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────────────── */}
      <UrgentCtaBand
        heading="Need Help Communicating with a Doctor in Vietnam?"
        subheading="Tell VietClinics what medical support you need, and our team will help you prepare questions, arrange suitable communication support, and coordinate your next steps with clinics, doctors or hospitals in Vietnam."
        testId="med-translator-final-cta"
      />

      <Footer />
    </div>
  );
};

export default MedicalTranslatorVietnamPage;

// Steps component re-exported for parity with sibling pages (unused locally)
export { UrgentSteps };
